import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketService } from '../services/ticketService';
import { bidService } from '../services/bidService';
import Sidebar from '../components/Sidebar';

const JobExplore = () => {
  const { currentUser, userData } = useAuth();
  const [openTickets, setOpenTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [myBids, setMyBids] = useState([]);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showExistingBidModal, setShowExistingBidModal] = useState(false);
  const [existingBid, setExistingBid] = useState(null);
  const [bidForm, setBidForm] = useState({
    amount: '',
    description: '',
    estimatedTime: '',
    startDate: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (currentUser && userData?.role === 'serviceProvider') {
      // Subscribe to tickets open to bid
      const unsubscribeTickets = ticketService.subscribeToOpenBidTickets((tickets) => {
        setOpenTickets(tickets || []);
        setLoading(false);
      });

      // Subscribe to user's bids
      const unsubscribeBids = bidService.subscribeToBidsByProvider(currentUser.uid, (bids) => {
        setMyBids(bids || []);
      });

      return () => {
        unsubscribeTickets();
        unsubscribeBids();
      };
    }
  }, [currentUser, userData]);

  // Ensure myBids state is properly synchronized
  useEffect(() => {
    if (currentUser && userData?.role === 'serviceProvider') {
      // Fetch initial bids data to ensure state is synchronized
      const fetchInitialBids = async () => {
        try {
          const bids = await bidService.getBidsByProvider(currentUser.uid);
          setMyBids(bids || []);
        } catch (error) {
          console.error('Error fetching initial bids:', error);
        }
      };
      
      fetchInitialBids();
    }
  }, [currentUser, userData]);

  const filteredTickets = openTickets.filter(ticket => {
    const matchesSearch = searchText === '' || 
      ticket.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.propertyName?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || 
      ticket.category?.toLowerCase() === categoryFilter.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  const getCategories = () => {
    const categories = new Set();
    openTickets.forEach(ticket => {
      if (ticket.category) {
        categories.add(ticket.category);
      }
    });
    return ['all', ...Array.from(categories).sort()];
  };

  const hasBidOnTicket = (ticketId) => {
    const hasBid = myBids.some(bid => bid.ticketId === ticketId);
    console.log(`Checking bid for ticket ${ticketId}:`, hasBid, 'myBids:', myBids);
    return hasBid;
  };

  const getBidStatus = (ticketId) => {
    const bid = myBids.find(b => b.ticketId === ticketId);
    if (!bid) return null;
    return bid.status;
  };

  const handleBidSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTicket || !currentUser) return;

    setSubmitting(true);
    try {
      await bidService.createBid({
        ticketId: selectedTicket.id,
        providerId: currentUser.uid,
        providerName: userData?.displayName || userData?.name || 'Service Provider',
        providerEmail: userData?.email || currentUser.email,
        providerCompany: userData?.company || '',
        amount: parseFloat(bidForm.amount),
        description: bidForm.description,
        estimatedTime: bidForm.estimatedTime,
        startDate: bidForm.startDate,
        ticketTitle: selectedTicket.title,
        propertyName: selectedTicket.propertyName
      });

      setShowBidModal(false);
      setSelectedTicket(null);
      setBidForm({ amount: '', description: '', estimatedTime: '', startDate: '' });
      alert('Bid submitted successfully!');
    } catch (error) {
      console.error('Error submitting bid:', error);
      
      // Handle specific error for duplicate bids
      if (error.message.includes('already submitted a bid')) {
        alert('You have already submitted a bid for this ticket. Only one bid per ticket is allowed.');
      } else {
        alert('Failed to submit bid. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openBidModal = (ticket) => {
    // Check if user already has a bid on this ticket
    if (hasBidOnTicket(ticket.id)) {
      alert('You have already submitted a bid for this ticket. Only one bid per ticket is allowed.');
      return;
    }
    
    setSelectedTicket(ticket);
    setShowBidModal(true);
  };

  const viewExistingBid = async (ticket) => {
    try {
      const bid = await bidService.getExistingBid(ticket.id, currentUser.uid);
      if (bid) {
        setExistingBid(bid);
        setShowExistingBidModal(true);
      }
    } catch (error) {
      console.error('Error fetching existing bid:', error);
      alert('Failed to load bid details. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (userData?.role !== 'serviceProvider') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">Access Denied</h3>
          <p className="text-slate-600 mb-6">This page is only available for service providers.</p>
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors duration-200"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-300 border-t-amber-600 mx-auto"></div>
          <p className="mt-6 text-slate-600 font-medium">Loading tickets open to bidding...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1 ml-72 -ml-8">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Job Explore</h1>
                <p className="text-sm text-gray-500">Browse and bid on tickets opened to bidding</p>
              </div>
              <div className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="p-8">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Important Information</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    You can only submit one bid per ticket. Once submitted, you cannot modify or submit another bid for the same ticket. 
                    Review the job details carefully before submitting your proposal.
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search by title, description, or property..."
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    {getCategories().map((category) => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Job Listings */}
            <div className="space-y-6">
              {filteredTickets.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-10 text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">No Tickets Open to Bidding</h3>
                  <p className="text-slate-600 mb-4">Currently there are no maintenance tickets open for bidding. Check back later or contact property owners to express your interest in their projects.</p>
                  <div className="flex items-center justify-center space-x-2 text-sm text-slate-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>New opportunities appear here when property owners open tickets to bidding</span>
                  </div>
                </div>
              ) : (
                filteredTickets.map((ticket) => {
                  const hasBid = hasBidOnTicket(ticket.id);
                  const bidStatus = getBidStatus(ticket.id);
                  
                  return (
                    <div key={ticket.id} className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">
                              {ticket.title || 'Maintenance Request'}
                            </h3>
                            <p className="text-sm text-slate-600 mb-3">
                              {ticket.description || 'No description provided'}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-slate-500">
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                {ticket.propertyName || 'Unknown Property'}
                              </span>
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {ticket.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                              </span>
                              {ticket.category && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                                  {ticket.category}
                                </span>
                              )}
                              {ticket.isOpenToBid && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Open to Bid
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-end space-y-3">
                            {hasBid ? (
                              <div className="text-right">
                                <div className={`px-3 py-1 rounded-full border text-xs font-medium ${getStatusColor(bidStatus)} mb-2`}>
                                  {bidStatus === 'pending' && 'Bid Submitted'}
                                  {bidStatus === 'approved' && 'Bid Approved'}
                                  {bidStatus === 'rejected' && 'Bid Rejected'}
                                </div>
                                <button
                                  onClick={() => viewExistingBid(ticket)}
                                  className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-300 transition-colors duration-200"
                                >
                                  View Bid
                                </button>
                                <p className="text-xs text-slate-500 mt-2">
                                  {bidStatus === 'pending' && 'Waiting for owner response'}
                                  {bidStatus === 'approved' && 'You got the job!'}
                                  {bidStatus === 'rejected' && 'Another provider was selected'}
                                </p>
                              </div>
                            ) : (
                              <button
                                onClick={() => openBidModal(ticket)}
                                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors duration-200"
                              >
                                Submit Bid
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bid Modal */}
      {showBidModal && selectedTicket && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="px-6 py-5 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">Submit Bid</h3>
                  <button
                    onClick={() => setShowBidModal(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Submit your bid for: {selectedTicket.title || 'Maintenance Request'}
                </p>
              </div>
              
              <form onSubmit={handleBidSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Bid Amount ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={bidForm.amount}
                      onChange={(e) => setBidForm({ ...bidForm, amount: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Work Description
                    </label>
                    <textarea
                      required
                      value={bidForm.description}
                      onChange={(e) => setBidForm({ ...bidForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      rows="3"
                      placeholder="Describe the work you'll perform..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Estimated Time
                    </label>
                    <input
                      type="text"
                      value={bidForm.estimatedTime}
                      onChange={(e) => setBidForm({ ...bidForm, estimatedTime: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="e.g., 2-3 days, 1 week"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Preferred Start Date
                    </label>
                    <input
                      type="date"
                      value={bidForm.startDate}
                      onChange={(e) => setBidForm({ ...bidForm, startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowBidModal(false)}
                    className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-3 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {submitting ? 'Submitting...' : 'Submit Bid'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Existing Bid Modal */}
      {showExistingBidModal && existingBid && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="px-6 py-5 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">Your Bid Details</h3>
                  <button
                    onClick={() => setShowExistingBidModal(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Review your submitted bid for this ticket
                </p>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Bid Amount
                    </label>
                    <p className="text-lg font-semibold text-slate-900">
                      ${existingBid.amount?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Work Description
                    </label>
                    <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">
                      {existingBid.description || 'No description provided'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Estimated Time
                    </label>
                    <p className="text-slate-700">
                      {existingBid.estimatedTime || 'Not specified'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Preferred Start Date
                    </label>
                    <p className="text-slate-700">
                      {existingBid.startDate ? new Date(existingBid.startDate).toLocaleDateString() : 'Not specified'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Bid Status
                    </label>
                    <div className={`inline-flex px-3 py-1 rounded-full border text-xs font-medium ${getStatusColor(existingBid.status)}`}>
                      {existingBid.status === 'pending' && 'Pending Review'}
                      {existingBid.status === 'approved' && 'Approved'}
                      {existingBid.status === 'rejected' && 'Rejected'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Submitted On
                    </label>
                    <p className="text-slate-700">
                      {existingBid.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </p>
                  </div>
                </div>
                
                <div className="pt-6">
                  <button
                    onClick={() => setShowExistingBidModal(false)}
                    className="w-full px-4 py-3 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobExplore;

